import { c as _c } from "react/compiler-runtime";
import { OptimizedImage } from '@/components/images/optimized-image';
import { SettledSeal } from '@/components/market/settled-seal';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';

// Leaf module for the shared outcome row/card sub-components. Lives apart from
// `other-outcomes-table` so `other-outcome-card` can use these without the
// table↔card import cycle (table → card → these parts).

interface OutcomeThumbProps {
  marketPubkey: string;
  index: number;
  label: string;
  imageUrl: string | null;
  imageNsfw: boolean;
  /** Tailwind size class, e.g. `size-8`. */
  size: string;
  showSettledBadge?: boolean;
}

/** Outcome thumbnail (NSFW-aware, hue-tinted) shared by the desktop row + mobile card. */
export function OutcomeThumb(t0) {
  const $ = _c(21);
  const {
    marketPubkey,
    index,
    label,
    imageUrl,
    imageNsfw,
    size,
    showSettledBadge: t1
  } = t0;
  const showSettledBadge = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== index || $[1] !== label) {
    t2 = {
      title: label,
      index
    };
    $[0] = index;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const colors = useOutcomeColor(t2);
  let t3;
  if ($[3] !== colors.cta || $[4] !== size) {
    t3 = cn("relative flex shrink-0 items-center justify-center rounded-panel-inset", size, colors.cta);
    $[3] = colors.cta;
    $[4] = size;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = imageUrl ?? null;
  let t5;
  if ($[6] !== colors.hue || $[7] !== imageNsfw || $[8] !== index || $[9] !== marketPubkey || $[10] !== t4) {
    t5 = {
      kind: "outcome",
      marketPubkey,
      outcomeIndex: index,
      hue: colors.hue,
      imageUrl: t4,
      nsfw: imageNsfw
    };
    $[6] = colors.hue;
    $[7] = imageNsfw;
    $[8] = index;
    $[9] = marketPubkey;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== size || $[13] !== t5) {
    t6 = <OptimizedImage variant="thumbnail" entity={t5} alt="" size={size} rounded="rounded-panel-inset" preview={false} loadReveal="none" className="size-full object-cover" />;
    $[12] = size;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== showSettledBadge) {
    t7 = showSettledBadge ? <SettledSeal className="pointer-events-none absolute -bottom-1.5 -right-1.5 z-10 size-5" /> : null;
    $[15] = showSettledBadge;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t3 || $[18] !== t6 || $[19] !== t7) {
    t8 = <span className={t3}>{t6}{t7}</span>;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

/** Thin neutral progress bar reflecting an outcome's final share. */
export function FinalShareBar(t0) {
  const $ = _c(2);
  const {
    pct
  } = t0;
  const fill = Math.min(100, Math.max(0, pct));
  const t1 = `${String(fill)}%`;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-background-tertiary"><span className="block h-full rounded-full bg-foreground-tertiary" style={{
        width: t1
      }} /></span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}